import CheckLightResume from './ChecklightResume';
import LidecoSemaforo from './LidecoSemaforo';
import Card from '../../../components/card/Card';
import Button from '../../../components/button/Button';
import { riskData } from '../../../types/interfaces';
import { APPLICATION_STATUS, RISK_CODES } from '../../../utils/Constants';
import { useState, type CSSProperties } from 'react';
import P from '../../../components/texts/p/P';
import TextArea from '../../../components/textarea/TextArea';
import Modal from '../../../components/modal/modal';
import { updateApplication } from '../../../services/ApplicationService';
import { Toast, useToast } from '../../../components/toast/toast';
import { useLocation, useNavigate } from 'react-router-dom';
import Spinner from '../../../components/Spinner/spinner';
import {
  navigateAfterFlowAction,
  type DetailLocationState,
} from '../../../utils/listNavigationState';
import useRole from '../../../hooks/useRole';
import { useConfig } from '../../../context/ConfigContext';

/** Figma Atlantic Semáforo footer: real CSS border (DS secondary uses inset shadow / border:none). */
const figmaUyOutlineBtn: CSSProperties = {
  background: '#FFFFFF',
  color: '#E72419',
  border: '1px solid #E72419',
  borderRadius: 6,
  width: 244,
  padding: '15px 18px',
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  fontFamily: 'Inter, sans-serif',
};
/** Figma Continuar: solid dark, not DS primary red. */
const figmaUyContinuarBtn: CSSProperties = {
  background: '#231F20',
  color: '#FFFFFF',
  border: '1px solid #231F20',
  borderRadius: 6,
  width: 200,
  padding: '15px 22px',
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  fontFamily: 'Inter, sans-serif',
};

interface CheckLightProps {
  handleNextStep: () => void;
  riskData: riskData | undefined;
  isLoading: boolean;
  handleRiskData: Function;
  handleUpdateDebts?: Function;
  handleRiskDataAndDebts?: Function;
  cuit: string | undefined;
  applicationId: string | undefined;
}

const CheckLight: React.FC<CheckLightProps> = ({
  handleNextStep,
  riskData,
  isLoading,
  handleRiskData,
  handleUpdateDebts,
  handleRiskDataAndDebts,
  cuit,
  applicationId,
}) => {
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as DetailLocationState;
  const { canTransitionRisk } = useRole();
  const config = useConfig();
  const isUruguayEnabled = config?.isUruguayEnabled === true;
  const paso1Codes = isUruguayEnabled
    ? [RISK_CODES.lideco]
    : [RISK_CODES.nosis, RISK_CODES.casfog, RISK_CODES.bcra];
  const onRefreshSemaforo = () => {
    if (isUruguayEnabled) {
      handleRiskData(paso1Codes, true);
    } else if (handleRiskDataAndDebts) {
      handleRiskDataAndDebts();
    } else {
      handleRiskData(paso1Codes, true);
    }
  };
  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApplication = (status: string) => {
    setIsSendingApplication(true);
    updateApplication(
      cuit || '',
      { status_code: status, comment: modalText },
      applicationId || ''
    ).then((response) => {
      const mapStatus = new Map();
      mapStatus.set(
        APPLICATION_STATUS.approved.label,
        'Aceptaste la documentación. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.rejected.label,
        'Declinaste la solicitud. El productor será notificado.'
      );
      mapStatus.set(
        APPLICATION_STATUS.pending.label,
        'Solicitaste cambios en la solicitud. El productor será notificado.'
      );

      if (response.status === 200) {
        useToast.success({
          title: mapStatus.get(status),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigateAfterFlowAction(navigate, location.pathname, locationState);
        }, 1000);
      }
      setIsSendingApplication(false);
    });
  };
  return (
    <Card>
      <Toast id='application-toast' />
      <Modal
        isOpen={isRejectApplication}
        onClose={() => setIsRejectApplication(false)}
        title={'Declinar o solicitar cambios'}
        primaryText='Solicitar cambios'
        primaryVariant='primary'
        secondaryText='Declinar solicitud'
        secondaryVariant='secondary'
        onSecondaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.rejected.label);
        }}
        onPrimaryAction={() => {
          setIsRejectApplication(false);
          handleApplication(APPLICATION_STATUS.pending.label);
        }}
        children={renderRejectModal()}
      />
      <Card
        // UY horizontal inset lives on RiskAnalysis step-body wrapper
        // (shared with Carga de balance / post-balance / resumen).
        spacing={{ left: isUruguayEnabled ? '0' : '6' }}
      >
        {isUruguayEnabled ? (
          <LidecoSemaforo
            riskData={Array.isArray(riskData) ? riskData : (riskData as any)}
            isLoading={isLoading}
            onRefresh={onRefreshSemaforo}
          />
        ) : (
          <CheckLightResume
            data={riskData}
            showBalanceInfo={false}
            isLoading={isLoading}
            handleRiskData={handleRiskData}
            handleUpdateDebts={handleUpdateDebts}
            handleRiskDataAndDebts={handleRiskDataAndDebts}
            riskCodes={[RISK_CODES.nosis, RISK_CODES.casfog, RISK_CODES.bcra]}
            debts={riskData?.debts || []}
            hideInfo={false}
          />
        )}
        <Card
          display='flex'
          margin={{ top: isUruguayEnabled ? '0' : '4' }}
          style={isUruguayEnabled ? { paddingTop: 1, alignItems: 'center' } : undefined}
          justifyContent='space-between'
        >
          {canTransitionRisk &&
            (isUruguayEnabled ? (
              <button
                type='button'
                id='checkRisk-button-reject'
                onClick={() => setIsRejectApplication(true)}
                disabled={isSendingApplication || isLoading}
                style={{ ...figmaUyOutlineBtn, position: 'relative', left: -4 }}
              >
                {isSendingApplication ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  'Declinar o solicitar cambios'
                )}
              </button>
            ) : (
              <Card width='280px'>
                <Button
                  id='checkRisk-button-reject'
                  size='lg'
                  variant='secondary'
                  onClick={() => setIsRejectApplication(true)}
                  fullWidth
                  disabled={isSendingApplication || isLoading}
                >
                  {isSendingApplication ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Declinar o solicitar cambios'
                  )}
                </Button>
              </Card>
            ))}
          <Card
            display='flex'
            justifyContent='flex-end'
            gap={isUruguayEnabled ? '18px' : '12px'}
            width={isUruguayEnabled ? 'auto' : '210px'}
            style={isUruguayEnabled ? { marginLeft: 'auto', position: 'relative', left: 2.5 } : undefined}
          >
            {isUruguayEnabled && (
              <button
                type='button'
                id='checkRisk-button-guardar'
                onClick={() => handleRiskData(paso1Codes, true)}
                disabled={isSendingApplication || isLoading}
                style={{ ...figmaUyOutlineBtn, width: 200 }}
              >
                {isLoading ? <Spinner size='sm' variant='primary' /> : 'Guardar'}
              </button>
            )}
            {isUruguayEnabled ? (
              <button
                type='button'
                id='client-button-save'
                onClick={handleNextStep}
                disabled={isSendingApplication || isLoading}
                style={figmaUyContinuarBtn}
              >
                {isSendingApplication ? <Spinner size='sm' variant='primary' /> : 'Continuar'}
              </button>
            ) : (
              <Button
                id='client-button-save'
                size='lg'
                variant='primary'
                fullWidth
                onClick={handleNextStep}
                disabled={isSendingApplication || isLoading}
              >
                {isSendingApplication ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  'Continuar'
                )}
              </Button>
            )}
          </Card>
        </Card>
      </Card>
    </Card>
  );
};

export default CheckLight;
